import { getPreventDownload, store, subscribeToResize } from '@msc-b2c/shared-utils';

let cleanupVideoResizeSubscription: (() => void) | null = null;

export function initializeVideoSettings() {
  function setVideoAttributes() {
    const videoElement = document.querySelector('.cmp-media__video') as HTMLVideoElement;
    if (!videoElement) return;

    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const shouldAutoplay = isMobile
      ? videoElement.dataset['autoplayMobile'] === 'true'
      : videoElement.dataset['autoplayDesktop'] === 'true';

    const isMuted = videoElement.dataset['isMuted'] === 'true';
    // force videos to play without go into full screen (ios mostly)
    videoElement.playsInline = true;

    // Prevent easy downloading
    if (getPreventDownload()) {
      videoElement.setAttribute('controlsList', 'nodownload nofullscreen noremoteplayback');

      videoElement.addEventListener('contextmenu', (e) => {
        e.preventDefault();
      });
    }

    // Only update mute if different
    if (videoElement.muted !== isMuted) {
      videoElement.muted = isMuted;
    }

    // Only change autoplay behavior if needed
    const isCurrentlyPlaying = !videoElement.paused && !videoElement.ended;

    if (shouldAutoplay && !isCurrentlyPlaying) {
      videoElement.play().catch((error) => {
        console.warn('Autoplay failed:', error);
      });
    } else if (!shouldAutoplay && isCurrentlyPlaying) {
      videoElement.pause();
    }

    stopVideoWhenOutOfView(shouldAutoplay, isMobile);
  }

  function stopVideoWhenOutOfView(shouldAutoplay: boolean, isMobile: boolean) {
    const videoElement = document.querySelector('.cmp-media__video') as HTMLVideoElement;
    if (!videoElement) return;

    // parallax handles video in its utils
    const isPartOfParallax = videoElement.closest('.parallax') !== null;
    if (isPartOfParallax) return;

    // if video goes out of view (user scrolls) the video stops
    // if it goes in view again check if it should autoplay so we can start it again
    // userPaused tracks whether the user manually paused — prevents auto-resume on scroll back
    let userPaused = false;
    let isProgrammaticPause = false;
    videoElement.addEventListener('pause', () => {
      if (!videoElement.ended && !isProgrammaticPause) userPaused = true;
    });
    videoElement.addEventListener('play', () => {
      userPaused = false;
    });

    const handleVideoIntersection = (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      const stopThreshold = isMobile ? 0.75 : 0.5;
      if (entry.intersectionRatio < stopThreshold) {
        if (!videoElement.paused) {
          isProgrammaticPause = true;
          videoElement.pause();
          setTimeout(() => {
            isProgrammaticPause = false;
          }, 0);
        }
      } else if (shouldAutoplay && videoElement.paused && !userPaused)
        videoElement
          .play()
          .catch((error) =>
            console.error(`Error (${error}) in video play after intersection observer pausing`)
          );
    };

    const observer = new IntersectionObserver(handleVideoIntersection, {
      threshold: Array.from(Array(101).keys(), (x) => x / 100),
    });

    observer.observe(videoElement);

    return () => {
      observer.unobserve(videoElement);
      observer.disconnect();
    };
  }

  setVideoAttributes();

  const tryApplyWhenConfigReady = () => {
    const config = store.getState().genericConfigs?.configs?.b2cCommerceConfig;
    if (config && typeof config.preventDownload === 'boolean') {
      setVideoAttributes();
      return true;
    }
    return false;
  };
  if (!tryApplyWhenConfigReady()) {
    const unsubscribe = store.subscribe(() => {
      if (tryApplyWhenConfigReady()) unsubscribe();
    });
  }

  // Debounce the resize event to avoid excessive calls
  let resizeTimeout: number;
  cleanupVideoResizeSubscription?.();
  cleanupVideoResizeSubscription = subscribeToResize(() => {
    const videoElement = document.querySelector('.cmp-media__video') as HTMLVideoElement | null;
    if (!videoElement || !videoElement.isConnected) {
      cleanupVideoResizeSubscription?.();
      cleanupVideoResizeSubscription = null;
      return;
    }
    clearTimeout(resizeTimeout);
    resizeTimeout = window.setTimeout(setVideoAttributes, 200);
  });
}

window.onload = () => {
  initializeVideoSettings();
};
